import { FC, ComponentProps } from "react";
import styled, { css } from "styled-components";
import { ButtonSpinner, PrimaryButton, StyledButton } from "./PrimaryButton";
import { Icon } from "../Icon";
import { ButtonVariant } from "./types";
import { primaryActive, primaryInactive } from "../../typography/alpha";
import "../../designTokens/variables.css";

const StyledSecondaryButton = styled(StyledButton)<SecondaryButtonProps>`
  :disabled {
    cursor: not-allowed;
    background: var(--color-primary-100);
    border: 1px solid var(--color-primary-200);
    ${primaryInactive.body3}
    *:not(.spinner) {
      fill: var(--color-primary-200);
    }
  }

  :not(:disabled) {
    ${({ variant }) => {
      if (variant === "DANGER") {
        return css`
          --active-color: var(--color-error-600);
          background: var(--color-error-100);
          border: 1px solid var(--color-error-600);
          color: var(--color-error-600);
          *:not(.spinner) {
            fill: var(--color-error-600);
          }
          &:hover {
            background: var(--color-error-200);
            color: var(--color-error-500);
            border: 1px solid var(--color-error-500);
            *:not(.spinner) {
              fill: var(--color-error-500);
            }
          }
        `;
      } else {
        return css`
          --active-color: var(--color-secondary-800);
          background: var(--color-secondary-100);
          border: 1px solid var(--color-primary-500);
          ${primaryActive.body3}
          *:not(.spinner) {
            fill: var(--color-primary-900);
          }
          &:hover {
            background: var(--color-secondary-200);
            color: var(--color-primary-800);
            border: 1px solid var(--color-primary-800);
            *:not(.spinner) {
              fill: var(--color-primary-800);
            }
          }
        `;
      }
    }}

    &:active {
      background: var(--color-neutral-000-full);
      color: var(--active-color);
      border: 1px solid var(--active-color);
      *:not(.spinner) {
        fill: var(--active-color);
      }
    }
  }
`;

type SecondaryButtonProps = ComponentProps<typeof PrimaryButton>;

export const SecondaryButton: FC<SecondaryButtonProps> = ({
  children,
  iconName,
  secondaryIconName,
  variant = ButtonVariant.DEFAULT,
  disabled,
  loading,
  ...buttonProps
}) => {
  return (
    <StyledSecondaryButton
      {...buttonProps}
      disabled={disabled || variant === ButtonVariant.DISABLED}
      variant={disabled ? ButtonVariant.DISABLED : variant}
    >
      {loading && <ButtonSpinner />}
      {iconName && <Icon iconName={iconName} iconSize="small" />}
      <span>{children}</span>
      {secondaryIconName && (
        <Icon iconName={secondaryIconName} iconSize="small" />
      )}
    </StyledSecondaryButton>
  );
};
